import { LightstreamerClient, Subscription } from 'lightstreamer-client-web/lightstreamer-core.esm';
import moment from 'moment';
import applicationSettings from '../../../Core/Settings/ApplicationSettings';
import { MarketPriceModel, updateMarketPrices, updateMarketPricesDebounced } from '../../../Redux/Slices/MarketPricesSlice';
import applicationInitializationService from '../ApplicationInitializationService';
import loggingService from '../Logging/LoggingService';

class LightstreamerRealtimeMarketPricesService {
    lightstreamerClient: any;

    lightstreamerSubscription: any;

    storeIdToCodesMap: { [key: string]: string[] } = {};

    accessToken: string = '';

    symbolToMarketPriceMap: { [key: string]: MarketPriceModel } = {};

    cachedMarketPriceUpdates: MarketPriceModel[] = [];

    updateInterval?: NodeJS.Timer;

    statusCheckInterval?: NodeJS.Timer;

    today = new Date();

    private adapterSet = 'MYSTONEX_V1';

    private dataAdapter = 'PRICES_V1';

    private lighstreamerStatusCodes = {
        connecting: ['CONNECTING', 'CONNECTED:STREAM-SENSING'],
        connected: ['CONNECTED:STREAM-SENSING', 'CONNECTED:WS-STREAMING', 'CONNECTED:HTTP-STREAMING', 'CONNECTED:WS-POLLING', 'CONNECTED:HTTP-POLLING'],
        disconnected: ['STALLED', 'DISCONNECTED:WILL-RETRY', 'DISCONNECTED:TRYING-RECOVERY', 'DISCONNECTED'],
    };

    private dataFieldsMappings = {
        Currency: 'CURRENCY',
        ExpirationDate: 'EXPIR_DATE',
        ExchangeSymbol: 'PROV_SYMB',
        SettlementPrice: 'SETTLE',
        OpenPrice: 'OPEN_PRC',
        LatestPrice: 'TRDPRC_1',
        HighPrice: 'HIGH_1',
        LowPrice: 'LOW_1',
        BidPrice: 'BID',
        BidSize: 'BIDSIZE',
        AskPrice: 'ASK',
        AskSize: 'ASKSIZE',
        PercentChange: 'PCTCHNG',
        PreviousSettlementPrice: 'HST_CLOSE',
        PreviousSettlementDate: 'HSTCLSDATE',
        NetChange: 'NETCHNG_1',
        Volume: 'ACVOL_1',
        OpenInterest: 'OPINT_1',
        OpenInterestNetChange: 'OPINTNC',
        LastSessionVolume: 'HST_VOL',
        LastSessionHighPrice: 'PRV_HIGH',
        LastSessionLowPrice: 'PRV_LOW',
        LatestPriceDate: 'TRADE_DATE',
        LatestPriceTime: 'TRDTIM_1',
        SettlementPriceDate: 'SETTLEDATE',
        SettlementPriceTime: 'SETTLE_TIM',
        SettlementNetChange: 'SETL_NCHNG',
        // TradeRegistrationPrice: 'IRGPRC',
        TradeRegistrationVolume: 'BLKVOLUM',
    };

    start = (storeId: string, reutersInstrumentCodes: string[], doNotRemoveExistingCodes = false): void => {
        try {
            const codes = reutersInstrumentCodes.filter((x) => !!x);
            this.storeIdToCodesMap[storeId] = doNotRemoveExistingCodes ? [...(this.storeIdToCodesMap[storeId] ?? []), ...codes] : codes;

            // Stop any existing subscriptions.
            if (this.lightstreamerSubscription) {
                try {
                    this.lightstreamerClient?.unsubscribe(this.lightstreamerSubscription);
                } catch (error) {
                    loggingService.trackException(error as Error, 'Error unsubscribing from Lightstreamer.');
                }
            }

            if (!this.lightstreamerClient) {
                this.accessToken = applicationInitializationService.getStore().getState()?.user.accessToken ?? '';

                // Start a new connection with all of the codes.
                this.lightstreamerClient = new LightstreamerClient(applicationSettings.api?.mystonexLightstreamerUri, this.adapterSet);
                this.lightstreamerClient.connectionDetails.setUser(applicationInitializationService.getStore().getState()?.user.userName ?? '');
                this.lightstreamerClient.connectionDetails.setPassword(this.accessToken);

                this.lightstreamerClient.connect();

                loggingService.trackTrace('Starting Lightstreamer connection.');
            }

            this.lightstreamerSubscription = new Subscription('MERGE', this.getAllCodes(), Object.values(this.dataFieldsMappings));
            this.lightstreamerSubscription.setDataAdapter(this.dataAdapter);
            this.lightstreamerSubscription.setRequestedSnapshot('yes');

            this.lightstreamerSubscription.addListener({
                onItemUpdate: (item: any) => {
                    const marketPrice: MarketPriceModel = {
                        reutersInstrumentCode: item.getItemName().replace('ID.', ''),
                        expirationDate: item.getValue(this.dataFieldsMappings.ExpirationDate),
                        exchangeSymbol: item.getValue(this.dataFieldsMappings.ExchangeSymbol),
                        currency: item.getValue(this.dataFieldsMappings.Currency),
                        latestPriceAsOfDate: LightstreamerRealtimeMarketPricesService.toDate(
                            item.getValue(this.dataFieldsMappings.LatestPriceDate),
                            item.getValue(this.dataFieldsMappings.LatestPriceTime),
                        ),
                        settlementPriceAsOfDate: LightstreamerRealtimeMarketPricesService.toDate(
                            item.getValue(this.dataFieldsMappings.SettlementPriceDate),
                            item.getValue(this.dataFieldsMappings.SettlementPriceTime),
                        ),
                        settlementPrice: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.SettlementPrice)),
                        openPrice: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.OpenPrice)),
                        latestPrice: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.LatestPrice)),
                        highPrice: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.HighPrice)),
                        lowPrice: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.LowPrice)),
                        bidPrice: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.BidPrice)),
                        bidSize: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.BidSize)),
                        askPrice: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.AskPrice)),
                        askSize: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.AskSize)),
                        percentChange: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.PercentChange)),
                        previousSettlementPrice: LightstreamerRealtimeMarketPricesService.toNumber(
                            item.getValue(this.dataFieldsMappings.PreviousSettlementPrice),
                        ),
                        netChange: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.NetChange)),
                        volume: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.Volume)),
                        openInterest: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.OpenInterest)),
                        openInterestNetChange: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.OpenInterestNetChange)),
                        lastSessionVolume: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.LastSessionVolume)),
                        lastSessionHighPrice: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.LastSessionHighPrice)),
                        lastSessionLowPrice: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.LastSessionLowPrice)),
                        settlementNetChange: LightstreamerRealtimeMarketPricesService.toNumber(item.getValue(this.dataFieldsMappings.SettlementNetChange)),
                        // tradeRegistrationPrice: LightstreamerRealtimeMarketPricesService.toNumber(
                        //     item.getValue(this.dataFieldsMappings.TradeRegistrationPrice),
                        // ),
                        tradeRegistrationVolume: LightstreamerRealtimeMarketPricesService.toNumber(
                            item.getValue(this.dataFieldsMappings.TradeRegistrationVolume),
                        ),
                    };

                    this.cachedMarketPriceUpdates.push(marketPrice);
                    this.symbolToMarketPriceMap[marketPrice.reutersInstrumentCode] = marketPrice;
                },
            });

            this.lightstreamerClient.subscribe(this.lightstreamerSubscription);

            loggingService.trackTrace(`Starting Lightstreamer subscription: ${this.getAllCodes()}`);

            if (this.updateInterval) {
                clearInterval(this.updateInterval);
            }

            if (this.statusCheckInterval) {
                clearInterval(this.statusCheckInterval);
            }

            // We only updates the stores on an interval as to not cause too many re-renders.
            this.updateInterval = setInterval(() => {
                if (this.cachedMarketPriceUpdates.length > 0) {
                    const updates = [...this.cachedMarketPriceUpdates];

                    this.cachedMarketPriceUpdates = [];
                    this.getAllStoreIds().forEach((updatedStoreId) => {
                        applicationInitializationService.getStore(updatedStoreId).dispatch(updateMarketPrices(updates));
                        applicationInitializationService.getStore(updatedStoreId).dispatch(updateMarketPricesDebounced());
                    });
                }
            }, 250);

            // Check the status on an interval.
            let counter = 0;
            this.statusCheckInterval = setInterval(() => {
                const status = this.lightstreamerClient?.getStatus();

                if (!this.lighstreamerStatusCodes.connected.some((x) => x === status)) {
                    // Only log non-connected statuses on interval.
                    loggingService.trackTrace(`Lightstreamer status: ${status}`);

                    this.lightstreamerClient = new LightstreamerClient(applicationSettings.api?.ravenLightstreamerUri, this.adapterSet);
                    this.lightstreamerClient.connectionDetails.setUser(applicationInitializationService.getStore().getState()?.user.userGuid ?? '');
                    this.lightstreamerClient.connectionDetails.setPassword(this.accessToken);

                    this.lightstreamerClient.connect();
                } else if (counter % 10 === 0) {
                    // Every 10th interval, check the access token.
                    counter = 0; // Prevent number overflow.

                    // Check if the access token changed and update it. Reconnections usually use a session id internal
                    // to lightstreamer, however if the server is restarted, the session id is lost and the access token
                    // is used instead to re-establish the connection.
                    const newAccessToken = applicationInitializationService.getStore().getState()?.user.accessToken ?? '';
                    if (newAccessToken !== this.accessToken) {
                        this.accessToken = newAccessToken;
                        this.lightstreamerClient.connectionDetails.setPassword(this.accessToken);
                    }
                }

                counter += 1;
            }, 1000);
        } catch (error) {
            loggingService.trackException(error as Error, `Error starting Lighstreamer connection to ${this.adapterSet}/${this.dataAdapter}.`);
        }
    };

    stop = (storeId: string): void => {
        try {
            delete this.storeIdToCodesMap[storeId];

            // If there are no more codes to retrieve, stop the master connection, otherwise, keep using it.
            if (this.getAllCodes().length === 0) {
                if (this.lightstreamerSubscription) {
                    this.lightstreamerClient?.unsubscribe(this.lightstreamerSubscription);
                    this.lightstreamerSubscription = undefined;
                }

                this.lightstreamerClient?.disconnect();
                this.lightstreamerClient = undefined;

                if (this.updateInterval) {
                    clearInterval(this.updateInterval);
                    this.updateInterval = undefined;
                }

                if (this.statusCheckInterval) {
                    clearInterval(this.statusCheckInterval);
                    this.statusCheckInterval = undefined;
                }
            }
        } catch (error) {
            loggingService.trackException(error as Error, `Error stopping Lighstreamer connection to ${this.adapterSet}/${this.dataAdapter}.`);
        }
    };

    private getAllStoreIds = (): string[] => Object.entries(this.storeIdToCodesMap).flatMap(([key]) => key);

    private getAllCodes = (): string[] =>
        Object.entries(this.storeIdToCodesMap)
            .flatMap(([, value]) => value)
            .map((x) => `ID.${x}`);

    private static toNumber = (value: any): number | undefined => (value === null || value === undefined || value === '' ? undefined : +value);

    private static toDate = (dateValue?: string, timeValue?: string): string | undefined => {
        if (!dateValue || !timeValue) {
            return undefined;
        }

        const dateTimeCombined = `${dateValue} ${timeValue.split(':').slice(0, 3).join(':')}`;
        const parsedDate = moment(dateTimeCombined, 'DD MMM YYYY HH:mm:ss');

        return parsedDate.isValid() ? parsedDate.toISOString() : undefined;
    };
}

const lightstreamerRealtimeMarketPricesService = new LightstreamerRealtimeMarketPricesService();

export default lightstreamerRealtimeMarketPricesService;
